"use client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { AuthError } from "@stadium/services/error-reporting/utils";
import { setContext } from "@stadium/services/apollo-client";
import { useAuth0V2 } from "@stadium/services/auth0";
import { getAuth } from "@stadium/redux-utils/selectors";
import { authActions } from "@stadium/redux-utils/slices";
import { userCookie, selectedWorkspaceSlugCookie } from "@stadium/helpers/cookie";
import { getImpersonationParams } from "@stadium/helpers/impersonation";
import MaxAttemptsNotifier from "@stadium/helpers/max-attemps-notifier";
import { matchesUrl } from "@stadium/helpers/dom";

import useStadiumSharedGlobalConfigContext from "@stadium/shared/src/hooks/use-global-context";

import {
  apolloClient as client,
  setApolloInstanceAuthLinkWithConfig as setAuthLinkWithConfig,
} from "@stadium/services/apollo-client/utils/instance";
import { userIdTracker } from "@stadium/services/google-analytics";

import { orderServiceInstance as axiosInstance } from "@stadium/services/axios";

import useWindowStorage, { SESSION_STORAGE } from "@stadium/hooks/lib/use-window-storage";

import Loaders from "@stadium/ui/src/components/loaders/loaders";

import { useAuthContext, AuthContext } from "@stadium/services/auth/client";

import { getWorkspaceSlugFromStoreSlug } from "@stadium/helpers/store";

import { STORES } from "@stadium/shared/src/constants/brands";
import { MY_WORKSPACE_SLUG, WORKSPACE_SLUG_REQUEST_HEADER_KEY } from "@stadium/constants";
import {
  getRoutesWithWorkspaceSlug,
  RESTRICTED_ROUTES_FOR_WORKSPACE_SLUG,
  STORE_HOME_PAGE_PATHNAME,
  STORE_CREATE_PAGE_PATHNAME,
  STORE_ORDER_REDEMPTION_PAGE_PATHNAME,
  OLD_STORE_HOME_PAGE_PATHNAME,
  OLD_STORE_CREATE_PAGE_PATHNAME,
  OLD_STORE_ORDER_REDEMPTION_PAGE_PATHNAME,
  SWAG_CREATION_ROUTE,
  STORE_WORKSPACE_PATHNAME,
  NEW_STORE_PATH_WITHOUT_WORKSPACE,
} from "@stadium/constants/routes";

const getWorkspaceSlugFromCookie = () => {
  try {
    if (typeof document === "undefined") return null;
    return selectedWorkspaceSlugCookie.get() ?? null;
  } catch {
    return null;
  }
};

export { useAuthContext };

const maxAttemptsNotifier = new MaxAttemptsNotifier(5, 5000);

const getWorkspaceSlugForHeader = (site) => {
  const isNewStoreWorkspaceRoute = matchesUrl({
    url: window.location.pathname,
    pathname: STORE_WORKSPACE_PATHNAME,
  });
  const isStoreRelatedRoute = matchesUrl({
    url: window.location.pathname,
    pathname: STORE_HOME_PAGE_PATHNAME,
  });
  const isOldStoreRelatedRoute = matchesUrl({
    url: window.location.pathname,
    pathname: OLD_STORE_HOME_PAGE_PATHNAME,
  });

  const isStoreCreationRoute = matchesUrl({
    url: window.location.pathname,
    pathname: STORE_CREATE_PAGE_PATHNAME,
  });

  const isOldStoreCreationRoute = matchesUrl({
    url: window.location.pathname,
    pathname: OLD_STORE_CREATE_PAGE_PATHNAME,
  });

  const isStoreOrderRedemptionRoute = matchesUrl({
    url: window.location.pathname,
    pathname: STORE_ORDER_REDEMPTION_PAGE_PATHNAME,
  });
  const isOldStoreOrderRedemptionRoute = matchesUrl({
    url: window.location.pathname,
    pathname: OLD_STORE_ORDER_REDEMPTION_PAGE_PATHNAME,
  });
  const isSwagCreationRoute = matchesUrl({
    url: window.location.pathname,
    pathname: SWAG_CREATION_ROUTE,
  });

  if (isNewStoreWorkspaceRoute) {
    const [, , , workspaceSlug] = window.location.pathname.split("/");
    return workspaceSlug || getWorkspaceSlugFromCookie() || MY_WORKSPACE_SLUG;
  }

  const isNewStorePathWithoutWorkspace = matchesUrl({
    url: window.location.pathname,
    pathname: NEW_STORE_PATH_WITHOUT_WORKSPACE,
  });
  if (isNewStorePathWithoutWorkspace) {
    return getWorkspaceSlugFromCookie() || MY_WORKSPACE_SLUG;
  }

  if (isOldStoreOrderRedemptionRoute && site === STORES) {
    const [, , , , slug] = window.location.pathname.split("/");
    return getWorkspaceSlugFromStoreSlug(slug, true);
  }
  if (isStoreOrderRedemptionRoute && site === STORES) {
    const [, , , slug] = window.location.pathname.split("/");
    return getWorkspaceSlugFromStoreSlug(slug, true);
  }

  if (isStoreRelatedRoute) {
    const [, , , slug] = window.location.pathname.split("/");
    return getWorkspaceSlugFromStoreSlug(slug);
  }

  if (isStoreCreationRoute) {
    const [, , , , slug] = window.location.pathname.split("/");
    return getWorkspaceSlugFromStoreSlug(slug);
  }

  if (isOldStoreCreationRoute) {
    const [, , , , , slug] = window.location.pathname.split("/");
    return getWorkspaceSlugFromStoreSlug(slug);
  }

  if (isOldStoreRelatedRoute) {
    const [, , , , slug] = window.location.pathname.split("/");
    return getWorkspaceSlugFromStoreSlug(slug);
  }

  if (isSwagCreationRoute) {
    const [, , workspaceSlug] = window.location.pathname.split("/");
    return workspaceSlug;
  }

  const [, slug] = window.location.pathname.split("/");

  const isRestrictedRoute = matchesUrl({
    url: window.location.pathname,
    pathname: RESTRICTED_ROUTES_FOR_WORKSPACE_SLUG,
  });

  if (isRestrictedRoute) {
    return getWorkspaceSlugFromCookie() || MY_WORKSPACE_SLUG;
  }

  const isWorkspaceEnabledUrl = matchesUrl({
    url: window.location.pathname,
    pathname: getRoutesWithWorkspaceSlug(),
  });

  const workspaceFromPath = isWorkspaceEnabledUrl ? slug : null;
  return workspaceFromPath || getWorkspaceSlugFromCookie() || MY_WORKSPACE_SLUG;
};

const AuthWrapper = ({ children, staticPage }) => {
  const { site } = useStadiumSharedGlobalConfigContext();
  const { getAccessTokenSilently, isAuthenticated, isLoading, user } = useAuth0V2();
  const {
    axiosInterceptorAdded,
    setAxiosInterceptorAdded,
    graphqlInterceptorAdded,
    setGraphqlInterceptorAdded,
  } = useAuthContext();
  const cookieUser = userCookie.get();
  const cookieAuthenticated = cookieUser?.authenticated;
  const { authenticated: storeAuthenticated, id: userId, email } = useSelector(getAuth);
  const dispatch = useDispatch();

  const [reloadingErrorOccured, setReloadingErrorOccured] = useWindowStorage(
    "reloading-error-occured",
    false,
    SESSION_STORAGE,
  );

  const authRef = useRef(isAuthenticated);
  authRef.current = isAuthenticated;

  const axiosInterceptor = useCallback(
    async (config) => {
      config.headers = {
        [WORKSPACE_SLUG_REQUEST_HEADER_KEY]: getWorkspaceSlugForHeader(site),
        ...config.headers,
      };
      if (!authRef.current) {
        return config;
      }
      if (config.url.includes("/mfa") || config.url.includes("/oauth/token")) {
        const mfaAccessToken = await getAccessTokenSilently({
          ignoreCache: true,
          audience: process.env.NEXT_PUBLIC_MFA_API_BASE_URL,
          scope: "read:authenticators enroll remove:authenticators",
        }).catch(() => null);
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${mfaAccessToken}`,
        };
        return config;
      }
      const accessToken = await getAccessTokenSilently().catch(() => null);
      if (accessToken) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${accessToken}`,
        };
      }
      return config;
    },
    [getAccessTokenSilently],
  );

  useEffect(() => {
    if (!user) return;

    maxAttemptsNotifier.notify("Updating authUser slice");
    if (user.email !== email) dispatch(authActions.initUser(user));
  }, [dispatch, user]);

  useEffect(() => {
    const interceptor = axiosInstance.interceptors.request.use(axiosInterceptor);
    setAxiosInterceptorAdded(true);

    return () => axiosInstance.interceptors.request.eject(interceptor);
  }, [axiosInterceptor]);

  const workspaceLink = useMemo(
    () =>
      setContext((_, { headers = {} }) => ({
        headers: {
          [WORKSPACE_SLUG_REQUEST_HEADER_KEY]: getWorkspaceSlugForHeader(site),
          ...headers,
        },
      })),
    [site],
  );

  const authLink = useMemo(
    () =>
      setContext(async (_, { headers }) => {
        let token;
        try {
          token = await getAccessTokenSilently();
        } catch {
          return { headers };
        }
        return {
          headers: {
            ...headers,
            Authorization: `Bearer ${token}`,
            ...getImpersonationParams(),
          },
        };
      }),
    [getAccessTokenSilently],
  );

  useEffect(() => {
    if (!isLoading) {
      client.setLink(
        setAuthLinkWithConfig({
          workspaceLink,
          authLink: isAuthenticated ? authLink : null,
          user,
        }),
      );
      setGraphqlInterceptorAdded(isAuthenticated);
    }
  }, [isLoading, isAuthenticated, workspaceLink, authLink, user]);

  useEffect(() => {
    if (isAuthenticated && userId) {
      userIdTracker(userId);
    }
  }, [isAuthenticated, userId]);

  useEffect(() => {
    if (storeAuthenticated && ((!isAuthenticated && !isLoading) || !cookieAuthenticated)) {
      if (!reloadingErrorOccured) {
        new AuthError("Reloading", { cookieAuthenticated, isAuthenticated, isLoading });
        setReloadingErrorOccured(true);
      }
      window.location.reload();
    }
  }, [isAuthenticated, storeAuthenticated, isLoading, cookieAuthenticated]);

  if (staticPage) {
    return children;
  }

  return isLoading || ((!graphqlInterceptorAdded || !axiosInterceptorAdded) && isAuthenticated) ? (
    <Loaders.PageLoader
      style={{
        position: "fixed",
        top: "0",
        left: "0",
        height: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    />
  ) : (
    children
  );
};

const AuthWrapperWithContext = (props) => {
  const [axiosInterceptorAdded, setAxiosInterceptorAdded] = useState(false);
  const [graphqlInterceptorAdded, setGraphqlInterceptorAdded] = useState(false);

  const value = useMemo(
    () => ({
      axiosInterceptorAdded,
      setAxiosInterceptorAdded,
      graphqlInterceptorAdded,
      setGraphqlInterceptorAdded,
    }),
    [axiosInterceptorAdded, graphqlInterceptorAdded],
  );

  return (
    <AuthContext.Provider value={value}>
      <AuthWrapper {...props} />
    </AuthContext.Provider>
  );
};

export default AuthWrapperWithContext;
